import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";

const SPEED_PX_PER_SEC = 40;
const PAUSE_MS = 1500;
const MIN_SLIDE_MS = 400;
const CYCLES = 3; // then rest at the start with the ellipsis (WCAG 2.2.2)
const FADE_PX = 12; // soft fade where the moving text is cut off
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

// While moving, the box fades out its cut-off edge(s): the mask is one fade
// wider than the box and slides with the text, so the edge where the text
// starts or ends stays crisp.
const FADE_MASK = {
  maskImage: `linear-gradient(to right, transparent, #000 ${FADE_PX}px, #000 calc(100% - ${FADE_PX}px), transparent)`,
  maskSize: `calc(100% + ${FADE_PX}px) 100%`,
  maskRepeat: "no-repeat",
  maskPosition: `${-FADE_PX}px 0px`,
};
const MOVING_STYLE = {
  ...FADE_MASK,
  WebkitMaskImage: FADE_MASK.maskImage,
  WebkitMaskSize: FADE_MASK.maskSize,
  WebkitMaskRepeat: FADE_MASK.maskRepeat,
  WebkitMaskPosition: FADE_MASK.maskPosition,
};

/**
 * One back-and-forth cycle for text that overflows by `distance` px:
 * hold at the start, slide left until the end shows, hold, slide back.
 * Offsets put the pauses inside one animation, so a tap can cancel and
 * restart it in one call. `fadeKeyframes` move the edge fade in step.
 */
export function tickerAnimation(distance) {
  const slide = Math.max(
    MIN_SLIDE_MS,
    Math.round((distance / SPEED_PX_PER_SEC) * 1000),
  );
  const duration = 2 * PAUSE_MS + 2 * slide;
  const at = (ms) => ms / duration;
  const cycle = (prop, from, to) => [
    { [prop]: from, offset: 0 },
    { [prop]: from, offset: at(PAUSE_MS), easing: "ease-in-out" },
    { [prop]: to, offset: at(PAUSE_MS + slide) },
    { [prop]: to, offset: at(2 * PAUSE_MS + slide), easing: "ease-in-out" },
    { [prop]: from, offset: 1 },
  ];
  return {
    duration,
    keyframes: cycle(
      "transform",
      "translateX(0px)",
      `translateX(${-distance}px)`,
    ),
    fadeKeyframes: cycle("maskPosition", `${-FADE_PX}px 0px`, "0px 0px"),
  };
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      !!window.matchMedia?.(REDUCED_MOTION)?.matches,
  );
  useEffect(() => {
    const mql = window.matchMedia?.(REDUCED_MOTION);
    if (!mql) return undefined;
    const onChange = () => setReduced(!!mql.matches);
    onChange();
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener?.(onChange);
    return () => mql.removeListener?.(onChange);
  }, []);
  return reduced;
}

/**
 * One line of text that fits as plain text. When it doesn't fit, it slides
 * like a ticker tape: to the end, pause, back, pause, three times, then rests
 * at the start with an ellipsis. A tap resets it to the start and runs it
 * again. Paused while off screen or while the tab is hidden. With reduced
 * motion it never moves; a tap shows the whole text on two lines instead.
 *
 * The text is in the DOM once (screen readers read it once) and in `title`.
 *
 * @param {string} text - the text to show
 * @param {boolean} interactive - false when the ticker sits inside a button
 *   or link: it then adds no tap handler of its own and the parent calls
 *   `ref.current.restart()` from its own tap
 */
const TickerText = forwardRef(function TickerText(
  { text, title = text, className, style, interactive = true, ...rest },
  ref,
) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const animRef = useRef(null); // the slide; its finish ends the run
  const fadeRef = useRef(null); // the edge fade, in step with the slide
  const autoStartedRef = useRef(false);
  const expandedRef = useRef(false);
  const visibleRef = useRef(typeof IntersectionObserver === "undefined");
  const [distance, setDistance] = useState(0);
  const [moving, setMoving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const canAnimate =
    !reducedMotion &&
    typeof Element !== "undefined" &&
    typeof Element.prototype.animate === "function";
  expandedRef.current = expanded;

  const measure = useCallback(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner || expandedRef.current) return;
    const over = Math.ceil(
      inner.getBoundingClientRect().width - outer.clientWidth,
    );
    setDistance(over > 1 ? over : 0);
  }, []);

  const syncPlayState = useCallback(() => {
    const run =
      visibleRef.current &&
      (typeof document === "undefined" || !document.hidden);
    for (const anim of [animRef.current, fadeRef.current]) {
      if (!anim) continue;
      if (run && anim.playState === "paused") anim.play();
      else if (!run && anim.playState !== "paused") anim.pause();
    }
  }, []);

  const cancel = useCallback(() => {
    const anim = animRef.current;
    const fade = fadeRef.current;
    animRef.current = null;
    fadeRef.current = null;
    if (anim) {
      anim.onfinish = null;
      anim.cancel();
    }
    fade?.cancel();
  }, []);

  const stop = useCallback(() => {
    cancel();
    setMoving(false);
  }, [cancel]);

  const start = useCallback(
    (d) => {
      cancel();
      const inner = innerRef.current;
      if (!inner || d <= 0) {
        setMoving(false);
        return;
      }
      const { keyframes, fadeKeyframes, duration } = tickerAnimation(d);
      const timing = { duration, iterations: CYCLES, easing: "linear" };
      const anim = inner.animate(keyframes, timing);
      anim.onfinish = () => {
        if (animRef.current !== anim) return;
        animRef.current = null;
        fadeRef.current = null;
        setMoving(false);
      };
      animRef.current = anim;
      try {
        fadeRef.current = outerRef.current?.animate(fadeKeyframes, timing);
      } catch {
        fadeRef.current = null; // no animatable mask: hard edges, still fine
      }
      setMoving(true);
      syncPlayState();
    },
    [cancel, syncPlayState],
  );

  // New text: measure again and allow one more automatic run
  useLayoutEffect(() => {
    autoStartedRef.current = false;
    expandedRef.current = false;
    setExpanded(false);
    measure();
  }, [text, measure]);

  useEffect(() => {
    if (!canAnimate || distance <= 0) {
      stop();
      return;
    }
    if (!autoStartedRef.current) {
      autoStartedRef.current = true;
      start(distance);
    } else if (animRef.current) {
      start(distance); // resized mid-run: slide the new distance
    }
  }, [canAnimate, distance, text, start, stop]);

  // Unmount (or StrictMode's test unmount): stop, and let a remount start again
  useEffect(
    () => () => {
      cancel();
      autoStartedRef.current = false;
    },
    [cancel],
  );

  // Re-measure on resize and once web fonts have loaded
  useEffect(() => {
    const outer = outerRef.current;
    if (!outer) return undefined;
    let alive = true;
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      ro.observe(outer);
    }
    document.fonts?.ready?.then(() => alive && measure());
    return () => {
      alive = false;
      ro?.disconnect();
    };
  }, [measure]);

  // Pause while off screen or while the tab is hidden
  useEffect(() => {
    const outer = outerRef.current;
    if (!outer || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver((entries) => {
      visibleRef.current = entries.some((e) => e.isIntersecting);
      syncPlayState();
    });
    io.observe(outer);
    return () => io.disconnect();
  }, [syncPlayState]);

  useEffect(() => {
    document.addEventListener("visibilitychange", syncPlayState);
    return () =>
      document.removeEventListener("visibilitychange", syncPlayState);
  }, [syncPlayState]);

  // Collapsing again: the width may have changed while it was expanded
  useEffect(() => {
    if (!expanded) measure();
  }, [expanded, measure]);

  const restart = useCallback(() => {
    if (!canAnimate) {
      if (distance > 0 || expandedRef.current) setExpanded((v) => !v);
      return;
    }
    if (distance > 0) start(distance);
  }, [canAnimate, distance, start]);

  useImperativeHandle(ref, () => ({ restart }), [restart]);

  const tappable = interactive && (distance > 0 || expanded);
  const state = expanded
    ? "expanded"
    : moving
      ? "moving"
      : distance > 0
        ? "rest"
        : "static";

  return (
    <span
      {...rest}
      ref={outerRef}
      title={title}
      data-ticker={state}
      onClick={interactive ? restart : undefined}
      style={moving ? { ...MOVING_STYLE, ...style } : style}
      className={cn(
        "block min-w-0 overflow-hidden",
        expanded
          ? "whitespace-normal break-words"
          : cn("whitespace-nowrap", moving ? "text-clip" : "text-ellipsis"),
        tappable && "cursor-pointer",
        className,
      )}
    >
      <span
        ref={innerRef}
        className={moving ? "inline-block will-change-transform" : undefined}
      >
        {text}
      </span>
    </span>
  );
});

export default TickerText;
